<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Diablo II: Resurrected – Terror Zone Introduction</h1>
    <p>Diablo II: Resurrected introduced Terror Zones in the 2.5 patch to provide players with new challenges and better leveling opportunities.</p>
    
    <h2>Terror Zone Overview:</h2>
    <ul>
      <li>Unlock Requirement: Each character must defeat Baal in a specific difficulty to unlock Terror Zones in that difficulty.</li>
      <li>Zone Rotation: The Terror Zone changes every hour, and players can reset the game to check for the new location.</li>
      <li>Monster Level Scaling: Enemies in a Terror Zone will have their levels increased based on the player’s level, at least +2 levels above the player’s, but not exceeding the maximum for that difficulty (the hell maximum level is 96). This allows for more experience gain and higher-level loot drops.</li>
      <li>Identifying a Terror Zone: When entering a Terror Zone, players will notice several indicators:</li>
      <ul>
        <li>A special icon next to monster names.</li>
        <li>On-screen text notifications.</li>
        <li>Unique sound effects.</li>
        <li>Changes in ambient lighting.</li>
        <li>Waypoint names in purple text if they are close to the Terror Zone.</li>
      </ul>
    </ul>

    <div id="slot-content_mpu" class="content-ad-mpu"/>
    
    <h2>Exclusive Loot – Sunder Charms:</h2>
    <p>Players in Terror Zones have the chance to obtain Sunder Charms, which allow them to break monster immunities, making them essential for many builds. Here are some notable Sunder Charms and their effects:</p>
    <ul>
        <li>
          <strong>The Flame Rift:</strong>
          <ul>
            <li>Breaks enemy <b class="fire-text">Fire</b> Immunity, but reduces the player’s fire resistance by 70-90%.
            </li>
            <li>Drop rate: 1/80
            </li>
          </ul>
        </li>
        <li>
          <strong>The Cold Rupture:</strong>
          <ul>
            <li>Breaks enemy <b class="cold-text">Cold</b> Immunity, but reduces the player’s cold resistance by 70-90%.
            </li>
            <li>Drop rate: 1/80
            </li>
          </ul>
        </li>
        <li>
          <strong>The Crack of the Heavens:</strong>
          <ul>
            <li>Breaks enemy <b class="lightning-text">Lightning</b> Immunity, but reduces the player’s lightning resistance by 70-90%.
            </li>
            <li>Drop rate: 1/120
            </li>
          </ul>
        </li>
        <li>
          <strong>The Rotting Fissure:</strong>
          <ul>
            <li>Breaks enemy <b class="poison-text">Poison</b> Immunity, but reduces the player’s poison resistance by 70-90%.
            </li>
            <li>Drop rate: 1/200
            </li>
          </ul>
        </li>
        <li>
          <strong>The Bone Break:</strong>
          <ul>
            <li>Breaks enemy <b class="physical-text">Physical</b> Immunity, but increases the player’s incoming physical damage by 10-20%.
            </li>
            <li>Drop rate: 1/400
            </li>
          </ul>
        </li>
        <li>
          <strong>The Black Cleft:</strong>
          <ul>
            <li>Breaks enemy <b class="magic-text">Magic</b> Immunity, but reduces the player’s magic resistance by 45-65%.
            </li>
            <li>Drop rate: 1/800
            </li>
          </ul>
        </li>
    </ul>
    <div>
      <img class="m-1 w-75" alt="sunder charms" src="@/assets/sunder-charms.png">
    </div>

        
    <h2>Recommended Builds for Farming All Terror Zones:</h2>
    <p>Choosing the right build is essential for surviving and maximizing efficiency in Terror Zones. Here are some of the most powerful and recommended builds:</p>
    <ul>
        <li>
          <strong>Mosaic Assassin:</strong> Uses the Mosaic runeword to maintain permanent elemental charge-up effects, allowing for devastating burst damage.
          <b-button
            class="m-2"
            variant="outline-light"
            href="/blog?build=mosaic-asn"
          >
            See Detail
          </b-button>
        </li>
        <div class="mb-4">
          <img class="m-1 w-75" alt="Mosaic Assassin screenshot" src="@/assets/builds/bd-mosaic-asn.png">
        </div>
        <li>
          <strong>Hammerdin (Blessed Hammer Paladin):</strong> A classic Paladin build that uses Blessed Hammer to deal high magic damage with a wide area of effect, making it strong in almost any situation.
          <b-button
            class="m-2"
            variant="outline-light"
            href="/blog?build=hammerdin"
          >
            See Detail
          </b-button>
        </li>
        <div class="mb-4">
          <img class="m-1 w-75" alt="Hammerdin screenshot" src="@/assets/builds/bd-hammerdin.png">
        </div>
        <li>
          <strong>Infinity Lightning/Nova Sorceress:</strong> Equipping the Infinity runeword greatly reduces enemy lightning resistance, allowing Lightning and Nova to clear enemies quickly.
          <b-button
            class="m-2"
            variant="outline-light"
            href="/blog?build=nova-sor"
          >
            See Nova Sorceress
          </b-button>
          <b-button
            class="m-2"
            variant="outline-light"
            href="/blog?build=lightning-sor"
          >
            See Lightning Sorceress
          </b-button>
        </li>
        <div class="mb-4">
          <img class="m-1 w-75" alt="Nova Sorceress screenshot" src="@/assets/builds/bd-nova-sor.png">
        </div>
        <li>
          <strong>Summon Necromancer:</strong> Relies on an army of Skeleton Warriors, Golems, and Revived minions, making it one of the safest and easiest builds for all players.
          <b-button
            class="m-2"
            variant="outline-light"
            href="/blog?build=summon-nec"
          >
            See Detail
          </b-button>
        </li>
        <div class="mb-4">
          <img class="m-1 w-75" alt="Summon Necromancer screenshot" src="@/assets/builds/bd-summon-nec.png">
        </div>
        <li>
          <strong>Physical Bow Amazon:</strong> A Bowazon focused on physical damage, using Multiple Shot and Strafe for consistent long-range damage output.
          <b-button
            class="m-2"
            variant="outline-light"
            href="/blog?build=bow-ama"
          >
            See Detail
          </b-button>
        </li>
        <div class="mb-4">
          <img class="m-1 w-75" alt="Physical Bow Amazon screenshot" src="@/assets/builds/bd-bow-ama.png">
        </div>
    </ul>
  </div>
</template>

<script>
import BlogList from '../BlogList.vue'

export default {
  name: 'Introduction',

  components: {
    BlogList,
  },
}
</script>